import { useState } from 'react';
import { useTheme } from '../context/ThemeContext';
import { Product } from '@/mocks/products';
import ProductFormModal from './ProductFormModal';
import { useProductCatalog } from '@/context/ProductCatalogContext';
import useScrollLock from '@/hooks/useScrollLock';

type FilterCat = 'all' | 'lithium' | 'agm' | 'gel';

export default function AdminProductsContent() {
  const { theme } = useTheme();
  const { products: productList, saveProduct, deleteProduct } = useProductCatalog();
  const dark = theme === 'dark';

  const [filter, setFilter] = useState<FilterCat>('all');
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState('');
  useScrollLock(Boolean(deleteConfirm));

  const card = dark ? 'bg-[#1C2B3A] border border-white/5' : 'bg-white border border-gray-200';
  const text = dark ? 'text-white' : 'text-gray-900';
  const muted = dark ? 'text-white/50' : 'text-gray-500';
  const divider = dark ? 'divide-white/5' : 'divide-gray-100';
  const inputCls = dark
    ? 'bg-[#0D1B2A] border-white/10 text-white placeholder-white/30 focus:border-[#F97316]'
    : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400 focus:border-[#F97316]';
  const filterBtn = (active: boolean) =>
    active
      ? 'bg-[#F97316] text-white'
      : dark
      ? 'bg-white/5 text-white/60 hover:text-white hover:bg-white/10'
      : 'bg-gray-100 text-gray-500 hover:text-gray-900 hover:bg-gray-200';

  const filtered = productList.filter((p) => {
    const matchCat = filter === 'all' || p.category === filter;
    const matchSearch = p.name.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  const handleSave = async (product: Product) => {
    setBusy(true);
    setSaveError('');
    try {
      await saveProduct(product);
      setModalOpen(false);
      setEditingProduct(null);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Unable to save product.');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (id: string) => {
    setBusy(true);
    setSaveError('');
    try {
      await deleteProduct(id);
      setDeleteConfirm(null);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Unable to delete product.');
    } finally {
      setBusy(false);
    }
  };

  const openEdit = (product: Product) => {
    setEditingProduct(product);
    setModalOpen(true);
  };

  const openNew = () => {
    setEditingProduct(null);
    setModalOpen(true);
  };

  const catLabel: Record<string, string> = { lithium: 'Lithium', agm: 'AGM', gel: 'Gel' };
  const catColor: Record<string, string> = {
    lithium: 'bg-[#F97316]/20 text-[#F97316]',
    agm: 'bg-green-500/20 text-green-400',
    gel: 'bg-yellow-500/20 text-yellow-400',
  };

  return (
    <div className="space-y-5">
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="flex flex-wrap gap-2">
          {(['all', 'lithium', 'agm', 'gel'] as FilterCat[]).map((cat) => (
            <button
              key={cat}
              onClick={() => setFilter(cat)}
              className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap capitalize ${filterBtn(filter === cat)}`}
            >
              {cat === 'all' ? 'All Products' : catLabel[cat]}
              <span className="ml-1.5 opacity-70">
                ({cat === 'all' ? productList.length : productList.filter((p) => p.category === cat).length})
              </span>
            </button>
          ))}
        </div>
        <div className="flex gap-3 items-center">
          <div className="relative">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
              <i className={`ri-search-line text-sm ${muted}`}></i>
            </div>
            <input
              type="text"
              placeholder="Search products..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={`pl-9 pr-4 py-2 text-sm rounded-lg border transition-colors outline-none w-52 ${inputCls}`}
            />
          </div>
          <button
            onClick={openNew}
            className="flex items-center gap-2 bg-[#F97316] text-white text-sm font-bold px-4 py-2 rounded-lg hover:bg-[#ea6a0a] transition-colors cursor-pointer whitespace-nowrap"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-add-line"></i>
            </div>
            Add Product
          </button>
        </div>
      </div>

      {saveError && (
        <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-3">
          {saveError}
        </div>
      )}

      {/* Products Table */}
      <div className={`${card} rounded-lg overflow-hidden`}>
        <div className={`grid grid-cols-12 gap-4 px-5 py-3 text-xs font-bold uppercase tracking-wide ${muted} border-b ${dark ? 'border-white/10' : 'border-gray-100'}`}>
          <div className="col-span-1"></div>
          <div className="col-span-4">Product</div>
          <div className="col-span-2">Category</div>
          <div className="col-span-1 text-center">Voltage</div>
          <div className="col-span-1 text-center">Capacity</div>
          <div className="col-span-1 text-center">Weight</div>
          <div className="col-span-1 text-right">Price</div>
          <div className="col-span-1 text-right">Actions</div>
        </div>

        <div className={`divide-y ${divider}`}>
          {filtered.length === 0 ? (
            <div className={`text-center py-16 ${muted}`}>
              <div className="w-10 h-10 flex items-center justify-center mx-auto mb-3">
                <i className="ri-inbox-line text-3xl"></i>
              </div>
              <p className="text-sm">No products found</p>
            </div>
          ) : (
            filtered.map((product) => (
              <div key={product.id} className={`grid grid-cols-12 gap-4 px-5 py-4 items-center transition-colors ${dark ? 'hover:bg-white/3' : 'hover:bg-gray-50'}`}>
                {/* Image */}
                <div className="col-span-1">
                  <div className="w-10 h-10 rounded-lg overflow-hidden bg-[#0D1B2A] shrink-0">
                    <img src={product.image} alt={product.name} className="w-full h-full object-cover object-top" />
                  </div>
                </div>
                {/* Name */}
                <div className="col-span-4">
                  <p className={`text-sm font-semibold ${text}`}>{product.name}</p>
                  <p className={`text-xs ${muted} mt-0.5 truncate`}>{product.tagline}</p>
                  {product.badge && (
                    <span className="inline-block mt-1 text-[10px] font-bold bg-[#F97316]/20 text-[#F97316] px-2 py-0.5 rounded-full">
                      {product.badge}
                    </span>
                  )}
                </div>
                {/* Category */}
                <div className="col-span-2">
                  <span className={`inline-block text-xs font-semibold px-2.5 py-1 rounded-full capitalize ${catColor[product.category]}`}>
                    {catLabel[product.category]}
                  </span>
                </div>
                {/* Voltage */}
                <div className={`col-span-1 text-center text-sm font-medium ${text}`}>{product.voltage}V</div>
                {/* Capacity */}
                <div className={`col-span-1 text-center text-sm font-medium ${text}`}>{product.capacity}Ah</div>
                {/* Weight */}
                <div className={`col-span-1 text-center text-sm font-medium ${text}`}>{product.weight} lbs</div>
                {/* Price */}
                <div className={`col-span-1 text-right text-sm font-black text-[#F97316]`}>${product.price.toLocaleString()}</div>
                {/* Actions */}
                <div className="col-span-1 flex items-center justify-end gap-1">
                  <button
                    onClick={() => openEdit(product)}
                    className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${dark ? 'text-white/50 hover:text-white hover:bg-white/10' : 'text-gray-400 hover:text-gray-900 hover:bg-gray-100'}`}
                    title="Edit"
                  >
                    <i className="ri-edit-line text-sm"></i>
                  </button>
                  <button
                    onClick={() => setDeleteConfirm(product.id)}
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-colors cursor-pointer"
                    title="Delete"
                  >
                    <i className="ri-delete-bin-line text-sm"></i>
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer count */}
        <div className={`px-5 py-3 border-t ${dark ? 'border-white/10' : 'border-gray-100'} flex items-center justify-between`}>
          <p className={`text-xs ${muted}`}>Showing {filtered.length} of {productList.length} products</p>
          <a
            href="/products"
            target="_blank"
            rel="noopener noreferrer"
            className={`text-xs font-medium text-[#F97316] hover:underline cursor-pointer whitespace-nowrap`}
          >
            View on site <i className="ri-external-link-line"></i>
          </a>
        </div>
      </div>

      {/* Delete Confirm Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className={`${dark ? 'bg-[#1C2B3A]' : 'bg-white'} rounded-lg p-6 w-80 shadow-xl`}>
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-red-500/20 mx-auto mb-4">
              <i className="ri-delete-bin-line text-red-400 text-2xl"></i>
            </div>
            <h3 className={`text-center font-bold text-base mb-2 ${text}`}>Delete Product?</h3>
            <p className={`text-center text-sm ${muted} mb-6`}>This action cannot be undone.</p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className={`flex-1 py-2.5 rounded-lg text-sm font-semibold cursor-pointer whitespace-nowrap border transition-colors ${dark ? 'border-white/20 text-white/70 hover:bg-white/10' : 'border-gray-200 text-gray-600 hover:bg-gray-100'}`}
              >
                Cancel
              </button>
              <button
                onClick={() => void handleDelete(deleteConfirm)}
                disabled={busy}
                className="flex-1 py-2.5 rounded-lg text-sm font-semibold bg-red-500 text-white hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Product Form Modal */}
      {modalOpen && (
        <ProductFormModal
          product={editingProduct}
          onSave={handleSave}
          onClose={() => { setModalOpen(false); setEditingProduct(null); }}
          dark={dark}
        />
      )}
    </div>
  );
}
